// components/EventEmails.js — the Emails tab on an event.  [marker: event-emails v1]
// Automatic emails (confirmation, sponsor thank-you, day-before reminder, after-event thank-you)
// plus emails to this event's guests, sent now or scheduled for later.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import toast from 'react-hot-toast';
import api from '../utils/api';

const KIND_INFO = {
  confirmation: { title: 'Ticket confirmation', what: 'Sent to each person as soon as their ticket is confirmed (paid, or free).' },
  sponsor_confirmation: { title: 'Sponsor thank-you', what: 'Sent to each sponsor of this event.' },
  day_before: { title: 'Reminder the day before', what: 'Sent to everyone with a ticket the day before the event.' },
  post_event: { title: 'Thank-you after the event', what: 'Sent the day after the event.' },
};
const LABELS = {
  when_text: 'When (date and time)', where_text: 'Where (with directions)', // [marker: confirm-polish v1]
  first_name: 'First name', last_name: 'Last name', full_name: 'Full name', event_name: 'Event name', event_date: 'Date',
  event_time_range: 'Time', venue: 'Venue', location_address: 'Address', directions_link: 'Directions link', calendar_link: 'Add-to-calendar link',
  org_name: 'Our name', org_phone: 'Our phone', org_email: 'Our email', ticket_number: 'Ticket number', ticket_type: 'Ticket type',
  amount_paid: 'Amount paid', ticket_link: 'Show-my-ticket link', sponsorship_name: 'Sponsorship level', sponsorship_amount: 'Sponsorship amount',
  included_tickets: 'Tickets included',
};
const TOOLBAR = [[{ header: [2, 3, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']];
const FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'link'];

const fmtWhen = d => d ? new Date(d).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
const time12 = t => { if (!t) return ''; const [h, m] = t.split(':'); const hh = parseInt(h, 10); return `${hh % 12 || 12}:${m} ${hh >= 12 ? 'PM' : 'AM'}`; };
const dayLabel = (iso, add) => { const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/); if (!m) return ''; const d = new Date(+m[1], +m[2] - 1, +m[3] + add); return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };

function Editor({ value, onChange, variables }) {
  const ref = useRef(null);
  const modules = useMemo(() => ({ toolbar: TOOLBAR }), []);
  const insert = (v) => {
    const q = ref.current && ref.current.getEditor(); if (!q) return;
    const r = q.getSelection(true) || { index: q.getLength() };
    q.insertText(r.index, `{{${v}}}`, 'user'); q.setSelection(r.index + v.length + 4, 0);
  };
  return (
    <div>
      <ReactQuill ref={ref} theme="snow" value={value} onChange={onChange} modules={modules} formats={FORMATS} style={{ background: '#fff' }} />
      {variables && variables.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--gray-500)' }}>
          Insert details that fill in automatically:{' '}
          {variables.map(v => (
            <button key={v} type="button" className="btn btn-ghost btn-sm" style={{ padding: '1px 6px', margin: '2px', border: '1px solid var(--gray-200)', fontSize: '0.72rem' }}
              onClick={() => insert(v)} title={`Inserts {{${v}}}`}>{LABELS[v] || v}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function TestSend({ eventId, kind, subject, body }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await api.post(`/event-emails/${eventId}/${kind}/test`, { to, subject, body_html: body }); toast.success(`Test sent to ${r.data.sentTo}.`); }
    catch (err) { toast.error(err.response?.data?.error || 'The test could not be sent.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="form-group" style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12, marginTop: 12 }} data-no-draft>
      <label className="form-label">Send yourself a test first</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input className="form-input" type="email" placeholder="you@njjba.com" value={to} onChange={e => setTo(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy || !to} onClick={send}>{busy ? 'Sending…' : 'Send test'}</button>
      </div>
      <p className="form-hint">Uses sample details (Sarah Cohen, ticket TKT-2026-0001) so you can see how it reads.</p>
    </div>
  );
}

function AutoEmailModal({ eventId, event, kind, setting, defaults, variables, standardSubject, onClose, onSaved }) {
  const info = KIND_INFO[kind];
  const [f, setF] = useState({ ...setting });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const vars = [...(variables.common || []), ...(variables[kind] || [])];
  const isConf = kind === 'confirmation';
  const showEditor = !isConf || f.mode === 'custom';
  const save = async () => {
    if (showEditor && !String(f.subject || '').trim()) return toast.error('Give the email a subject line.');
    setSaving(true);
    try {
      await api.put(`/event-emails/${eventId}/${kind}`, { ...f, enabled: isConf ? f.mode === 'custom' : f.enabled });
      toast.success('Saved.'); onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">{info.title} — {event.name}</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>{info.what}</p>
          {isConf ? (
            <div className="form-group">
              <label className="form-label">Which confirmation should this event use?</label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '6px 0' }}>
                <input type="radio" name="confmode" checked={f.mode !== 'custom'} onChange={() => set('mode', 'standard')} />
                <span>The standard confirmation <span style={{ color: 'var(--gray-500)' }}>(Settings → Email Templates{standardSubject ? `: "${standardSubject}"` : ''})</span></span>
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '6px 0' }}>
                <input type="radio" name="confmode" checked={f.mode === 'custom'} onChange={() => set('mode', 'custom')} />
                <span>A custom confirmation for this event only</span>
              </label>
            </div>
          ) : (
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
              <input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />
              {kind === 'sponsor_confirmation' ? 'Send sponsors a thank-you' : 'Send this email automatically'}
            </label>
          )}
          {kind === 'sponsor_confirmation' && (
            <div className="form-group"><label className="form-label">When</label>
              <select className="form-select" value={f.send_on} onChange={e => set('send_on', e.target.value)}>
                <option value="paid">When the sponsorship is paid</option>
                <option value="created">As soon as they sign up</option>
              </select></div>
          )}
          {(kind === 'day_before' || kind === 'post_event') && (
            <div className="form-row">
              <div className="form-group"><label className="form-label">Send at</label>
                <input className="form-input" type="time" value={f.send_time || '10:00'} onChange={e => set('send_time', e.target.value)} />
                <p className="form-hint">{kind === 'day_before' ? `On ${dayLabel(event.event_date, -1)}` : `On ${dayLabel(event.event_date, 1)}`}, New Jersey time.</p></div>
              {kind === 'post_event' && (
                <div className="form-group"><label className="form-label">Send to</label>
                  <select className="form-select" value={f.audience} onChange={e => set('audience', e.target.value)}>
                    <option value="all">Everyone with a ticket</option>
                    <option value="checked_in">Only people who checked in</option>
                  </select></div>
              )}
            </div>
          )}
          {showEditor && (<>
            <div className="form-group"><label className="form-label">Subject</label>
              <input className="form-input" value={f.subject || ''} onChange={e => set('subject', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Message</label>
              <Editor value={f.body_html || ''} onChange={v => set('body_html', v)} variables={vars} /></div>
            <div><button type="button" className="btn btn-ghost btn-sm" onClick={() => { set('subject', defaults[kind].subject); set('body_html', defaults[kind].body_html); }}>Start over from the suggested text</button></div>
            <TestSend eventId={eventId} kind={kind} subject={f.subject} body={f.body_html} />
          </>)}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function NewEventEmailModal({ eventId, event, audiences, variables, onClose, onSaved }) {
  const [f, setF] = useState({ audience: 'all', ticketTypeId: '', subject: '', body: '', when: 'now', sendAt: '' });
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    if (f.audience === 'ticket_type' && !f.ticketTypeId) { setCount(null); return; }
    let live = true;
    api.post(`/event-emails/${eventId}/count`, { audience: f.audience, ticketTypeId: f.ticketTypeId || null })
      .then(r => { if (live) setCount(r.data.count); }).catch(() => live && setCount(null));
    return () => { live = false; };
  }, [eventId, f.audience, f.ticketTypeId]);
  const submit = async () => {
    if (!f.subject.trim()) return toast.error('Give the email a subject line.');
    if (!f.body.replace(/<[^>]*>/g, '').trim()) return toast.error('Write the email first.');
    if (f.when === 'later' && !f.sendAt) return toast.error('Pick the date and time to send it.');
    const sendAt = f.when === 'later' ? new Date(f.sendAt).toISOString() : null;
    if (!sendAt && !window.confirm(`Send this email to ${count ?? 'the selected'} ${count === 1 ? 'person' : 'people'} now?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/event-emails/${eventId}/blasts`, { subject: f.subject, bodyHtml: f.body, audience: f.audience, ticketTypeId: f.ticketTypeId || null, sendAt });
      toast.success(sendAt ? `Scheduled for ${fmtWhen(sendAt)}.` : `Sending to ${r.data.recipientCount} ${r.data.recipientCount === 1 ? 'person' : 'people'}.`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'The email could not be sent.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">Email guests — {event.name}</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Send to</label>
              <select className="form-select" value={f.audience} onChange={e => set('audience', e.target.value)}>
                {Object.entries(audiences).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <p className="form-hint">{count === null ? ' ' : `${count} ${count === 1 ? 'person' : 'people'} (each email address once)`}</p></div>
            {f.audience === 'ticket_type' && (
              <div className="form-group"><label className="form-label">Ticket type</label>
                <select className="form-select" value={f.ticketTypeId} onChange={e => set('ticketTypeId', e.target.value)}>
                  <option value="">Choose…</option>
                  {(event.ticketTypes || []).map(tt => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
                </select></div>
            )}
          </div>
          <div className="form-group"><label className="form-label">Subject</label>
            <input className="form-input" value={f.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. Parking and arrival for tomorrow's briefing" /></div>
          <div className="form-group"><label className="form-label">Message</label>
            <Editor value={f.body} onChange={v => set('body', v)} variables={variables.common || []} /></div>
          <div className="form-group"><label className="form-label">When</label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' }}><input type="radio" name="when" checked={f.when === 'now'} onChange={() => set('when', 'now')} /> Send now</label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0', flexWrap: 'wrap' }}><input type="radio" name="when" checked={f.when === 'later'} onChange={() => set('when', 'later')} /> Send later on
              <input className="form-input" type="datetime-local" value={f.sendAt} onChange={e => { set('sendAt', e.target.value); set('when', 'later'); }} style={{ width: 'auto' }} /></label>
          </div>
          <TestSend eventId={eventId} kind="blast" subject={f.subject} body={f.body} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Working…' : f.when === 'later' ? 'Schedule email' : 'Send now'}</button>
        </div>
      </div>
    </div>
  );
}

export default function EventEmails({ eventId, event, canEdit, openKind, onOpened }) { // [marker: confirm-polish v1] openKind: open one email's editor straight away
  const [data, setData] = useState(null);
  const [blasts, setBlasts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get(`/event-emails/${eventId}`), api.get(`/event-emails/${eventId}/blasts`)]);
      setData(a.data); setBlasts(b.data.blasts || []);
    } catch (err) { toast.error(err.response?.data?.error || 'Could not load the event emails.'); }
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [preset, setPreset] = useState(null); // [marker: confirm-polish v1]
  useEffect(() => {
    if (data && openKind && canEdit) { setEditing(openKind); if (openKind === 'confirmation') setPreset({ mode: 'custom' }); if (onOpened) onOpened(); }
  }, [data, openKind]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="card"><div className="card-body"><p className="text-muted">Loading…</p></div></div>;
  const s = data.settings;
  const status = (k) => {
    const x = s[k];
    if (k === 'confirmation') return x.mode === 'custom' ? 'Custom email for this event' : 'Standard email';
    if (!x.enabled) return 'Off';
    if (k === 'sponsor_confirmation') return `On — sent ${x.send_on === 'created' ? 'when a sponsor signs up' : 'when a sponsorship is paid'}`;
    if (x.sent_at) return x.last_error ? `Not sent: ${x.last_error}` : `Sent ${fmtWhen(x.sent_at)}`;
    const day = k === 'day_before' ? dayLabel(data.event.event_date, -1) : dayLabel(data.event.event_date, 1);
    return `On — ${day} at ${time12(x.send_time || '10:00')}${k === 'day_before' ? ` to ${data.ticketHolders} ticket ${data.ticketHolders === 1 ? 'holder' : 'holders'} so far` : (x.audience === 'checked_in' ? ' to people who checked in' : ' to everyone with a ticket')}`;
  };
  const isOn = k => (k === 'confirmation' ? s[k].mode === 'custom' : s[k].enabled);
  const sendExisting = async () => {
    if (!window.confirm(`Send the thank-you to the ${data.sponsorsWaiting} sponsor(s) who haven't received it?`)) return;
    setBusy(true);
    try { const r = await api.post(`/event-emails/${eventId}/sponsor_confirmation/send-existing`); toast.success(`Sent ${r.data.sent}${r.data.failed ? `, ${r.data.failed} failed` : ''}.`); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not send.'); }
    finally { setBusy(false); }
  };
  const unschedule = async (b) => {
    try { await api.post(`/email-blasts/${b.id}/unschedule`); toast.success('Unscheduled. It is saved as a draft in Email Blasts.'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not unschedule.'); }
  };
  const audienceOf = b => { const m = b.target_metadata || {}; return data.audiences[m.audience || 'all'] || ''; };
  const KIND_FOR_BLAST = { day_before: 'Day-before reminder', post_event: 'After-event thank-you' };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div className="card">
        <div className="card-header"><span className="card-title">Automatic emails for this event</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          {Object.keys(KIND_INFO).map(k => (
            <div key={k} style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{KIND_INFO[k].title}
                  <span style={{ marginLeft: 8, fontSize: '0.7rem', padding: '1px 8px', borderRadius: 10, background: isOn(k) ? '#e7f6ec' : 'var(--gray-100)', color: isOn(k) ? '#1d7a3a' : 'var(--gray-500)' }}>
                    {k === 'confirmation' ? (isOn(k) ? 'Custom' : 'Standard') : (isOn(k) ? 'On' : 'Off')}</span></div>
                <div style={{ fontSize: '0.8rem', color: s[k].last_error ? 'var(--danger)' : 'var(--gray-500)' }}>{status(k)}</div>
                {k === 'sponsor_confirmation' && s[k].enabled && data.sponsorsWaiting > 0 && canEdit && (
                  <button type="button" className="btn btn-ghost btn-sm" style={{ padding: 0, marginTop: 4 }} disabled={busy} onClick={sendExisting}>
                    Also send it to the {data.sponsorsWaiting} sponsor{data.sponsorsWaiting === 1 ? '' : 's'} already listed</button>
                )}
              </div>
              {canEdit && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(k)}>{isOn(k) ? 'Edit' : 'Set up'}</button>}
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Emails to this event's guests</span>
          {canEdit && <button type="button" className="btn btn-primary btn-sm" onClick={() => setComposing(true)}>+ New email</button>}
        </div>
        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Subject</th><th>Sent to</th><th>Status</th><th>People</th><th>Opened</th><th></th></tr></thead>
            <tbody>
              {blasts.length === 0 ? (
                <tr><td colSpan={6}><div className="empty-state" style={{ padding: 20 }}><p>No emails sent to this event's guests yet.</p>
                  {canEdit && <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Use "+ New email" to send one now or schedule it for later.</p>}</div></td></tr>
              ) : blasts.map(b => (
                <tr key={b.id}>
                  <td><strong>{b.subject.replace(/{{\s*event_name\s*}}/g, event.name)}</strong>{b.automatic_kind && <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{KIND_FOR_BLAST[b.automatic_kind]} (automatic)</div>}</td>
                  <td style={{ fontSize: '0.82rem' }}>{audienceOf(b)}</td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {b.status === 'scheduled' ? `Scheduled for ${fmtWhen(b.scheduled_at)}` : b.status === 'sent' ? `Sent ${fmtWhen(b.sent_at)}` : b.status === 'sending' ? 'Sending…' : b.status}
                  </td>
                  <td>{b.recipient_count ?? ''}</td>
                  <td>{b.status === 'sent' ? b.open_count : ''}</td>
                  <td>{b.status === 'scheduled' && canEdit && <button type="button" className="btn btn-ghost btn-sm" onClick={() => unschedule(b)}>Unschedule</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <AutoEmailModal eventId={eventId} event={data.event} kind={editing} setting={preset ? { ...s[editing], ...preset } : s[editing]} defaults={data.defaults} variables={data.variables}
          standardSubject={data.standardConfirmationSubject} onClose={() => { setEditing(null); setPreset(null); }} onSaved={() => { setEditing(null); setPreset(null); load(); }} />
      )}
      {composing && (
        <NewEventEmailModal eventId={eventId} event={event} audiences={data.audiences} variables={data.variables}
          onClose={() => setComposing(false)} onSaved={() => { setComposing(false); load(); }} />
      )}
    </div>
  );
}
